import { useEffect, useMemo, useState, useCallback } from 'react';
import AuthenticatedLayout from '../Layouts/AuthenticatedLayout';
import { useAuth } from '../context/AuthContext';
import axios from '../axios';
import { Button } from '../shadcn/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../shadcn/ui/select';
import { useToast } from '../shadcn/ui/use-toast';

const CATEGORY_CHILD_KEYS = ['children', 'children_recursive', 'childrenRecursive'];

const getChildNodes = (node) => {
  if (!node) return [];
  for (const key of CATEGORY_CHILD_KEYS) {
    if (Array.isArray(node[key])) {
      return node[key];
    }
  }
  return [];
};

const normalizeCategoryNode = (node, trail = []) => {
  if (!node) return null;
  const normalized = {
    ...node,
    id: String(node.id),
  };

  const currentTrail = [...trail, normalized];
  normalized.children = getChildNodes(node)
    .map((child) => normalizeCategoryNode(child, currentTrail))
    .filter(Boolean);

  normalized.pathLabel = currentTrail.map((c) => c.name).join(' / ');
  return normalized;
};

const CategoryTreeList = ({ nodes, onAdd }) => {
  if (!nodes?.length) {
    return <p className="text-sm text-gray-500">Δεν υπάρχουν κατηγορίες.</p>;
  }

  return (
    <div className="space-y-2">
      {nodes.map((node) => (
        <div key={node.id} className="border rounded-md p-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">{node.name}</span>
            <Button size="sm" variant="outline" onClick={() => onAdd(node)}>
              Προσθήκη
            </Button>
          </div>
          {node.children?.length > 0 && (
            <div className="mt-2 ml-4 border-l pl-4 space-y-2">
              <CategoryTreeList nodes={node.children} onAdd={onAdd} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const MenuBuilderPage = ({
  storageKey = 'menuBuilderDraft',
  menuType = 'header',
  title = 'Διαμόρφωση Μενού',
  description = 'Επιλέξτε ποιες κατηγορίες θα εμφανίζονται στο μενού και προσαρμόστε τη σειρά/δομή.',
  allowDropdowns = true,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [pendingChildSelection, setPendingChildSelection] = useState({
    parentId: '',
    categoryId: '',
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(menuItems));
  }, [menuItems, storageKey]);

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get('/api/categories-full-tree');
      const normalized = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      const tree = normalized
        .map((node) => normalizeCategoryNode(node))
        .filter(Boolean);
      setCategoriesTree(tree);
    } catch (error) {
      console.error('Failed to load categories tree', error);
      toast({
        title: 'Σφάλμα',
        description: 'Αποτυχία φόρτωσης κατηγοριών.',
        variant: 'error',
      });
    } finally {
      setLoadingCategories(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchMenuLayout = useCallback(async () => {
    setLoadingLayout(true);
    try {
      const response = await axios.get('/api/menu-layout', {
        params: { type: menuType },
      });
      const items = Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data)
          ? response.data
          : [];
      setMenuItems(items);
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      console.error('Failed to load menu layout', error);
      toast({
        title: 'Σφάλμα',
        description: 'Αποτυχία φόρτωσης αποθηκευμένου μενού.',
        variant: 'error',
      });
    } finally {
      setLoadingLayout(false);
    }
  }, [menuType, storageKey, toast]);

  useEffect(() => {
    fetchMenuLayout();
  }, [fetchMenuLayout]);

  const flattenedCategories = useMemo(() => {
    const list = [];
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        list.push({ id: node.id, name: node.name, path: node.pathLabel || node.name });
        if (node.children?.length) traverse(node.children);
      });
    };
    traverse(categoriesTree);
    return list;
  }, [categoriesTree]);

  const findCategoryById = (id) =>
    flattenedCategories.find((category) => category.id === id);

  const handleAddTopLevel = (category) => {
    if (menuItems.find((item) => item.id === category.id)) {
      toast({
        title: 'Προειδοποίηση',
        description: 'Η κατηγορία υπάρχει ήδη στο μενού.',
        variant: 'default',
      });
      return;
    }

    setMenuItems((prev) => [
      ...prev,
      {
        id: category.id,
        label: category.name,
        path: category.pathLabel || category.name,
        children: [],
      },
    ]);
  };

  const handleRemoveItem = (targetId, parentId = null) => {
    setMenuItems((prev) => {
      if (!parentId) {
        return prev.filter((item) => item.id !== targetId);
      }

      return prev.map((item) => {
        if (item.id !== parentId) return item;
        return { ...item, children: item.children.filter((child) => child.id !== targetId) };
      });
    });
  };

  const handleAddChild = () => {
    if (!allowDropdowns) return;
    const { parentId, categoryId } = pendingChildSelection;
    if (!parentId || !categoryId) return;

    setMenuItems((prev) =>
      prev.map((item) => {
        if (item.id !== parentId) return item;
        if (item.children.find((child) => child.id === categoryId)) return item;

        const category = findCategoryById(categoryId);
        if (!category) return item;

        return {
          ...item,
          children: [
            ...item.children,
            {
              id: category.id,
              label: category.name,
              path: category.path,
            },
          ],
        };
      })
    );

    setPendingChildSelection({ parentId: '', categoryId: '' });
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (event) => event.preventDefault();
  const handleDrop = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    setMenuItems((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(draggedIndex, 1);
      updated.splice(index, 0, removed);
      return updated;
    });
    setDraggedIndex(null);
  };

  const handleSaveLayout = async () => {
    setIsSavingLayout(true);
    try {
      await axios.post('/api/menu-layout', {
        type: menuType,
        items: menuItems,
      });
      localStorage.setItem(storageKey, JSON.stringify(menuItems));
      toast({
        title: 'Αποθηκεύτηκε',
        description: 'Το μενού αποθηκεύτηκε επιτυχώς.',
      });
    } catch (error) {
      console.error('Failed to save menu layout', error);
      const message = error.response?.data?.message || 'Αποτυχία αποθήκευσης.';
      toast({
        title: 'Σφάλμα',
        description: message,
        variant: 'error',
      });
    } finally {
      setIsSavingLayout(false);
    }
  };

  return (
    <AuthenticatedLayout user={user}>
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-sm text-gray-500">
            {description}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Διαθέσιμες κατηγορίες</h2>
              <Button variant="outline" size="sm" onClick={fetchCategories} disabled={loadingCategories}>
                Επαναφόρτωση
              </Button>
            </div>
            <div className="mt-4 max-h-[600px] overflow-y-auto pr-2">
              {loadingCategories ? (
                <p className="text-sm text-gray-500">Φόρτωση κατηγοριών...</p>
              ) : (
                <CategoryTreeList nodes={categoriesTree} onAdd={handleAddTopLevel} />
              )}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Μενού ιστοσελίδας</h2>
              <Button size="sm" onClick={handleSaveLayout} disabled={isSavingLayout || loadingLayout}>
                {isSavingLayout ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
            <div className="mt-4 space-y-4">
              {loadingLayout ? (
                <p className="text-sm text-gray-500">Φόρτωση αποθηκευμένου μενού...</p>
              ) : menuItems.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν έχετε προσθέσει καμία κατηγορία στο μενού.</p>
              ) : (
                <div className="space-y-3">
                  {menuItems.map((item, index) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                      className="rounded-md border border-gray-200 p-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{item.label}</p>
                          <p className="text-xs text-gray-500">{item.path}</p>
                        </div>
                        <div className="space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setPendingChildSelection((prev) => ({
                                parentId: prev.parentId === item.id ? '' : item.id,
                                categoryId: '',
                              }))
                            }
                          >
                            Υπομενού
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRemoveItem(item.id)}>
                            Αφαίρεση
                          </Button>
                        </div>
                      </div>

                      {item.children.length > 0 && (
                        <div className="mt-3 space-y-2 border-t pt-2">
                          {item.children.map((child) => (
                            <div
                              key={child.id}
                              className="flex items-center justify-between rounded bg-gray-50 p-2 text-sm"
                            >
                              <span>{child.label}</span>
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => handleRemoveItem(child.id, item.id)}
                              >
                                Αφαίρεση
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {pendingChildSelection.parentId === item.id && (
                        <div className="mt-3 flex flex-col gap-2 rounded-md bg-gray-50 p-3">
                          <p className="text-sm font-medium">Προσθήκη υπομενού</p>
                          <Select
                            value={pendingChildSelection.categoryId}
                            onValueChange={(value) =>
                              setPendingChildSelection((prev) => ({ ...prev, categoryId: value }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Επιλέξτε κατηγορία" />
                            </SelectTrigger>
                            <SelectContent>
                              {flattenedCategories
                                .filter(
                                  (category) => category.id !== item.id &&
                                    !item.children.some((child) => child.id === category.id)
                                )
                                .map((category) => (
                                  <SelectItem key={category.id} value={category.id}>
                                    {category.path}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <div className="flex gap-2">
                            <Button disabled={!pendingChildSelection.categoryId} onClick={handleAddChild}>
                              Προσθήκη
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => setPendingChildSelection({ parentId: '', categoryId: '' })}
                            >
                              Άκυρο
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default MenuBuilderPage;
